'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  Package, Clock, CheckCircle, AlertCircle, Calendar, MapPin,
  Image, ChevronDown, ChevronUp, Zap, FileText, Download,
  LayoutDashboard, TrendingUp, AlertTriangle, ExternalLink,
  Receipt, ArrowRight, RefreshCw
} from 'lucide-react';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/components/PublicFooter';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import SupportTicketForm from './components/SupportTicketForm';
import InvoicesSection from './components/InvoicesSection';

interface Order {
  id: string;
  orderNumber: string;
  issueLabel: string;
  placement: string;
  regions: string[];
  term: 'one' | 'three';
  total: number;
  paymentStatus: 'paid' | 'pending' | 'failed';
  orderStatus: 'active' | 'completed' | 'processing';
  artworkStatus: 'approved' | 'pending_review' | 'awaiting_upload' | 'revision_requested';
  artworkReviewUrl: string | null;
  artworkDeadline: string;
  campaignDeliveryDate: string;
  createdAt: string;
  addDesign: boolean;
  paymentIntentId?: string | null;
  refundAmount?: number | null;
  refundedAt?: string | null;
  disputeId?: string | null;
  disputeStatus?: string | null;
  invoiceUrl?: string | null;
}

const placementNames: Record<string, string> = {
  standard: 'Standard Inside Page',
  ifc: 'Inside Front Cover',
  ibc: 'Inside Back Cover',
  bc: 'Back Cover',
  fc: 'Front Cover',
};

const paymentBadge: Record<string, { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'bg-green-500/15 text-green-400 border border-green-500/25' },
  pending: { label: 'Pending', className: 'bg-amber-500/15 text-amber-400 border border-amber-500/25' },
  failed: { label: 'Failed', className: 'bg-red-500/15 text-red-400 border border-red-500/25' },
};

const orderStatusBadge: Record<string, { label: string; className: string; icon: () => React.ReactNode }> = {
  active: { label: 'Active', className: 'bg-brand-600/15 text-brand-300 border border-brand-600/25', icon: () => <Clock size={11} /> },
  completed: { label: 'Completed', className: 'bg-green-500/15 text-green-400 border border-green-500/25', icon: () => <CheckCircle size={11} /> },
  processing: { label: 'Processing', className: 'bg-sky-500/15 text-sky-400 border border-sky-500/25', icon: () => <AlertCircle size={11} /> },
};

const artworkSteps = [
  { key: 'awaiting_upload', label: 'Upload Pending', step: 1 },
  { key: 'pending_review', label: 'In Review', step: 2 },
  { key: 'revision_requested', label: 'Revision Needed', step: 2 },
  { key: 'approved', label: 'Approved', step: 3 },
];

function ArtworkProgressBar({ status }: { status: Order['artworkStatus'] }) {
  const steps = [
    { label: 'Uploaded', done: status !== 'awaiting_upload' },
    { label: 'In Review', done: status === 'approved' },
    { label: 'Approved', done: status === 'approved' },
  ];
  const isRevision = status === 'revision_requested';

  return (
    <div className="mt-3">
      <div className="flex items-center gap-0">
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border transition-all ${
                  isRevision && i === 1
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : s.done
                    ? 'bg-green-500/20 border-green-500/40 text-green-400'
                    : i === 0 && status === 'awaiting_upload' ?'bg-amber-500/15 border-amber-500/40 text-amber-400'
                    : i === 1 && status === 'pending_review' ?'bg-sky-500/15 border-sky-500/40 text-sky-400' :'bg-white/5 border-white/15 text-slate-500'
                }`}
              >
                {s.done ? <CheckCircle size={12} /> : i + 1}
              </div>
              <span className="text-[10px] text-slate-500 whitespace-nowrap">{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-px mx-1 mb-4 transition-all ${
                  s.done ? 'bg-green-500/40' : 'bg-white/10'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function DeliveryTimeline({ order }: { order: Order }) {
  const milestones = [
    {
      label: 'Order Placed',
      date: order.createdAt,
      done: true,
      icon: <Package size={13} className="text-brand-400" />,
      color: 'brand',
    },
    {
      label: 'Payment Confirmed',
      date: order.paymentStatus === 'paid' ? order.createdAt : '—',
      done: order.paymentStatus === 'paid',
      icon: <Receipt size={13} className={order.paymentStatus === 'paid' ? 'text-green-400' : 'text-slate-500'} />,
      color: 'green',
    },
    {
      label: 'Artwork Deadline',
      date: order.artworkDeadline,
      done: order.artworkStatus === 'approved',
      icon: <Image size={13} className={order.artworkStatus === 'approved' ? 'text-green-400' : 'text-amber-400'} />,
      color: 'amber',
    },
    {
      label: 'Campaign Live',
      date: order.campaignDeliveryDate,
      done: order.orderStatus === 'completed',
      icon: <Zap size={13} className={order.orderStatus === 'completed' ? 'text-green-400' : 'text-slate-500'} />,
      color: 'green',
    },
  ];

  return (
    <div className="relative">
      <div className="absolute left-3 top-4 bottom-4 w-px bg-white/8" />
      <div className="space-y-4">
        {milestones.map((m) => (
          <div key={m.label} className="flex items-start gap-3 pl-1">
            <div
              className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center shrink-0 border ${
                m.done
                  ? 'bg-green-500/15 border-green-500/30' :'bg-white/5 border-white/15'
              }`}
            >
              {m.icon}
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <p className={`text-xs font-semibold ${m.done ? 'text-white' : 'text-slate-400'}`}>{m.label}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{m.date}</p>
            </div>
            {m.done && (
              <CheckCircle size={13} className="text-green-400 shrink-0 mt-0.5" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function InvoiceDownloadButton({ order }: { order: Order }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  if (order.invoiceUrl) {
    return (
      <a
        href={order.invoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Download PDF Invoice"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-medium text-slate-300 hover:bg-white/10 hover:text-white transition-all"
      >
        <Download size={12} />
        Invoice
        <ExternalLink size={10} className="opacity-60" />
      </a>
    );
  }

  const handleDownload = async () => {
    setStatus('loading');
    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/generate-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ orderId: order.id }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ZipAdBook-Invoice-${order.orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setStatus('idle');
    } catch (err) {
      console.error('PDF invoice download failed:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={status === 'loading'}
      title={status === 'error' ? 'Download failed — try again' : 'Download PDF Invoice'}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all disabled:opacity-50 ${
        status === 'error' ?'bg-red-500/15 border-red-500/25 text-red-400' :'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white'
      }`}
    >
      {status === 'loading' ? (
        <RefreshCw size={12} className="animate-spin" />
      ) : status === 'error' ? (
        <AlertCircle size={12} />
      ) : (
        <Download size={12} />
      )}
      {status === 'loading' ? 'Generating…' : status === 'error' ? 'Failed' : 'Invoice'}
    </button>
  );
}

function OrderCard({ order }: { order: Order }) {
  const [expanded, setExpanded] = useState(false);
  const pBadge = paymentBadge[order.paymentStatus] ?? paymentBadge['pending'];
  const sBadge = orderStatusBadge[order.orderStatus] ?? orderStatusBadge['processing'];

  const artworkStatusColor: Record<string, string> = {
    approved: 'text-green-400',
    pending_review: 'text-sky-400',
    awaiting_upload: 'text-amber-400',
    revision_requested: 'text-red-400',
  };
  const artworkLabel: Record<string, string> = {
    approved: 'Approved',
    pending_review: 'In Review',
    awaiting_upload: 'Awaiting Upload',
    revision_requested: 'Revision Needed',
  };

  return (
    <div className="glass-card rounded-2xl border border-white/8 overflow-hidden">
      {/* Header row */}
      <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-brand-600/15 border border-brand-600/20 flex items-center justify-center shrink-0">
            <FileText size={16} className="text-brand-400" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-white text-sm">{order.orderNumber}</p>
            <p className="text-xs text-slate-400 truncate">{order.issueLabel} · Placed {order.createdAt}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${sBadge.className}`}>
            {sBadge.icon()}
            {sBadge.label}
          </span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${pBadge.className}`}>
            {pBadge.label}
          </span>
          <span className="font-display font-bold text-brand-400 text-sm tabular-nums">
            ${order.total.toLocaleString()}{order.term === 'three' ? '/mo' : ''}
          </span>
          <InvoiceDownloadButton order={order} />
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-400 hover:text-white hover:bg-white/10 transition-all"
            aria-label={expanded ? 'Collapse order' : 'Expand order'}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Quick summary row */}
      <div className="px-5 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-white/8">
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Placement</p>
          <p className="text-xs text-white font-medium leading-snug">{placementNames[order.placement] || order.placement}</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Artwork</p>
          <p className={`text-xs font-semibold ${artworkStatusColor[order.artworkStatus]}`}>
            {artworkLabel[order.artworkStatus]}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Artwork Deadline</p>
          <div className="flex items-center gap-1 text-xs text-white">
            <Calendar size={11} className="text-amber-400 shrink-0" />
            {order.artworkDeadline}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Campaign Delivery</p>
          <div className="flex items-center gap-1 text-xs text-white">
            <Zap size={11} className="text-green-400 shrink-0" />
            {order.campaignDeliveryDate}
          </div>
        </div>
      </div>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-white/8 animate-fade-in">
          {/* Left: Artwork progress + regions */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Artwork Upload Progress</p>
            <ArtworkProgressBar status={order.artworkStatus} />

            {order.artworkStatus === 'awaiting_upload' && (
              <div className="mt-4 flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/8 border border-amber-500/20">
                <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-300 mb-0.5">Upload required by {order.artworkDeadline}</p>
                  <p className="text-[11px] text-slate-400">Missing this deadline may delay your campaign.</p>
                  <Link
                    href="/my-orders"
                    className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    Upload Artwork <ArrowRight size={11} />
                  </Link>
                </div>
              </div>
            )}

            {order.artworkStatus === 'revision_requested' && (
              <div className="mt-4 flex items-start gap-2.5 p-3 rounded-xl bg-red-500/8 border border-red-500/20">
                <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-red-300 mb-0.5">Revision requested by our team</p>
                  {order.artworkReviewUrl && (
                    <a
                      href={order.artworkReviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-red-400 hover:text-red-300 transition-colors"
                    >
                      View Feedback <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {order.artworkStatus === 'approved' && (
              <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-green-500/8 border border-green-500/20">
                <CheckCircle size={14} className="text-green-400 shrink-0" />
                <p className="text-xs font-semibold text-green-300">Artwork approved — you're all set!</p>
              </div>
            )}

            <div className="mt-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Regions</p>
              <div className="flex flex-wrap gap-1.5">
                {order.regions.map((r) => (
                  <span key={r} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-slate-300">
                    <MapPin size={9} className="text-brand-400" />
                    {r}
                  </span>
                ))}
              </div>
              {order.addDesign && (
                <p className="text-xs text-brand-400 mt-2 flex items-center gap-1">
                  <CheckCircle size={11} />
                  Design service included
                </p>
              )}
            </div>
          </div>

          {/* Right: Delivery timeline */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Delivery Timeline</p>
            <DeliveryTimeline order={order} />

            <div className="mt-5 p-3 rounded-xl bg-white/3 border border-white/8">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Campaign Details</p>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Term</span>
                  <span className="text-white font-medium">{order.term === 'three' ? '3-Issue Campaign' : 'Single Issue'}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Total</span>
                  <span className="font-display font-bold text-brand-400 tabular-nums">
                    ${order.total ? order.total.toLocaleString() : '—'}{order.term === 'three' ? '/mo' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrderDashboardPage() {
  const { user, loading, refreshSession } = useAuth();
  const router = useRouter();
  const [authChecked, setAuthChecked] = useState(false);
  const [resolvedUser, setResolvedUser] = useState<any>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed' | 'processing' | 'pending'>('all');

  const isVerifiedUser = (candidate: any) => Boolean(candidate?.email_confirmed_at || candidate?.confirmed_at);
  const verifyEmailPath = (email?: string) => `/verify-email${email ? `?email=${encodeURIComponent(email)}` : ''}`;

  useEffect(() => {
    let cancelled = false;
    const ensureAuth = async () => {
      if (loading) return;
      if (user) {
        if (!isVerifiedUser(user)) { router.replace(verifyEmailPath(user.email)); return; }
        if (!cancelled) { setResolvedUser(user); setAuthChecked(true); }
        return;
      }
      const refreshedUser = await refreshSession();
      if (cancelled) return;
      if (!refreshedUser) { router.replace('/sign-up-login'); return; }
      if (!isVerifiedUser(refreshedUser)) { router.replace(verifyEmailPath(refreshedUser.email)); return; }
      setResolvedUser(refreshedUser);
      setAuthChecked(true);
    };
    ensureAuth();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  useEffect(() => {
    if (!resolvedUser) return;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('orders')
          .select('*')
          .eq('user_id', resolvedUser.id)
          .order('created_at', { ascending: false });

        if (error) { console.error('Failed to load orders:', error.message); setOrders([]); }
        else {
          const mapped: Order[] = (data || []).map((row: any) => ({
            id: row.id,
            orderNumber: row.order_number,
            issueLabel: row.issue_label,
            placement: row.placement,
            regions: row.regions || [],
            term: row.term as 'one' | 'three',
            total: row.total,
            paymentStatus: row.payment_status as Order['paymentStatus'],
            orderStatus: row.order_status as Order['orderStatus'],
            artworkStatus: row.artwork_status as Order['artworkStatus'],
            artworkReviewUrl: row.artwork_review_url || null,
            artworkDeadline: row.artwork_deadline,
            campaignDeliveryDate: row.campaign_delivery_date,
            createdAt: row.created_at
              ? new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : '',
            addDesign: row.add_design,
            paymentIntentId: row.payment_intent_id || null,
            refundAmount: row.refund_amount ?? null,
            refundedAt: row.refunded_at ?? null,
            disputeId: row.dispute_id ?? null,
            disputeStatus: row.dispute_status ?? null,
            invoiceUrl: row.invoice_pdf_url ?? row.invoice_url ?? null,
          }));
          setOrders(mapped);
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();

    const supabase = createClient();
    const channel = supabase
      .channel('order-dashboard-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `user_id=eq.${resolvedUser.id}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as any;
            setOrders((prev) => [{
              id: row.id, orderNumber: row.order_number, issueLabel: row.issue_label,
              placement: row.placement, regions: row.regions || [], term: row.term,
              total: row.total, paymentStatus: row.payment_status, orderStatus: row.order_status,
              artworkStatus: row.artwork_status, artworkReviewUrl: row.artwork_review_url || null,
              artworkDeadline: row.artwork_deadline, campaignDeliveryDate: row.campaign_delivery_date,
              createdAt: row.created_at ? new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
              addDesign: row.add_design, paymentIntentId: row.payment_intent_id || null,
            }, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as any;
            setOrders((prev) => prev.map((o) => o.id === row.id ? {
              ...o, paymentStatus: row.payment_status, orderStatus: row.order_status,
              artworkStatus: row.artwork_status, artworkReviewUrl: row.artwork_review_url || null, total: row.total,
            } : o));
          } else if (payload.eventType === 'DELETE') {
            setOrders((prev) => prev.filter((o) => o.id !== (payload.old as any).id));
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [resolvedUser]);

  if (loading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-400 rounded-full animate-spin" />
      </div>
    );
  }
  if (!resolvedUser) return null;

  const activeOrders = orders.filter((o) => o.orderStatus === 'active' || o.orderStatus === 'processing');
  const completedOrders = orders.filter((o) => o.orderStatus === 'completed');
  const needsArtwork = orders.filter((o) => o.artworkStatus === 'awaiting_upload' || o.artworkStatus === 'revision_requested');
  const totalSpent = orders.reduce((s, o) => s + (o.total || 0), 0);

  const pendingOrders = orders.filter((o) => o.paymentStatus === 'pending');
  const filteredOrders =
    filterStatus === 'all'
      ? orders
      : filterStatus === 'pending'
        ? pendingOrders
        : orders.filter((o) => o.orderStatus === filterStatus);

  const filterOptions: { key: typeof filterStatus; label: string; count: number }[] = [
    { key: 'all', label: 'All Orders', count: orders.length },
    { key: 'active', label: 'Active', count: activeOrders.length },
    { key: 'processing', label: 'Processing', count: orders.filter((o) => o.orderStatus === 'processing').length },
    { key: 'pending', label: 'Pending', count: pendingOrders.length },
    { key: 'completed', label: 'Completed', count: completedOrders.length },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <PublicTopbar />
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Page header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-brand-600/20 border border-brand-600/30 flex items-center justify-center">
                <LayoutDashboard size={22} className="text-brand-400" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-800 text-white">Order Dashboard</h1>
                <p className="text-sm text-slate-400">Track artwork progress, delivery dates, and download invoices</p>
              </div>
            </div>
            <Link
              href="/order-wizard"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold btn-primary"
            >
              <Zap size={14} />
              Book an Ad
            </Link>
          </div>

          {/* Stats bento */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Orders', value: ordersLoading ? '—' : orders.length, icon: <Package size={16} className="text-brand-400" />, sub: 'all time' },
              { label: 'Active Campaigns', value: ordersLoading ? '—' : activeOrders.length, icon: <Clock size={16} className="text-sky-400" />, sub: 'in progress' },
              { label: 'Needs Artwork', value: ordersLoading ? '—' : needsArtwork.length, icon: <Image size={16} className="text-amber-400" />, sub: needsArtwork.length > 0 ? 'action required' : 'all clear' },
              { label: 'Total Invested', value: ordersLoading ? '—' : `$${totalSpent.toLocaleString()}`, icon: <TrendingUp size={16} className="text-green-400" />, sub: 'across all orders' },
            ].map((stat) => (
              <div key={stat.label} className="glass-card rounded-2xl p-4 border border-white/8">
                <div className="flex items-center gap-2 mb-2">
                  {stat.icon}
                  <span className="text-xs text-slate-400">{stat.label}</span>
                </div>
                <p className="font-display text-2xl font-700 text-white">{stat.value}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{stat.sub}</p>
              </div>
            ))}
          </div>

          {/* Artwork action alert */}
          {!ordersLoading && needsArtwork.length > 0 && (
            <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-amber-500/8 border border-amber-500/25">
              <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-300 mb-0.5">
                  {needsArtwork.length} order{needsArtwork.length > 1 ? 's' : ''} need{needsArtwork.length === 1 ? 's' : ''} artwork attention
                </p>
                <p className="text-xs text-slate-400">
                  {needsArtwork.map((o) => o.orderNumber).join(', ')} — expand the order below to take action.
                </p>
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div className="flex items-center gap-1 mb-6 bg-navy-800/60 rounded-xl p-1 w-fit border border-white/8 flex-wrap">
            {filterOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFilterStatus(opt.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer flex items-center gap-1.5 ${
                  filterStatus === opt.key
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {opt.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${filterStatus === opt.key ? 'bg-white/20' : 'bg-white/8'}`}>
                  {opt.count}
                </span>
              </button>
            ))}
          </div>

          {/* Orders list */}
          {ordersLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-400 rounded-full animate-spin" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="glass-card rounded-2xl p-14 border border-white/8 text-center">
              <Package size={40} className="text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-1 font-medium">
                {filterStatus === 'all' ? 'No orders yet' : `No ${filterStatus} orders`}
              </p>
              <p className="text-sm text-slate-500 mb-5">Start advertising to thousands of households across Florida and Puerto Rico.</p>
              <Link href="/order-wizard" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold btn-primary">
                <Zap size={14} />
                Book Your First Ad
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          {!ordersLoading && orders.length > 0 && (
            <div className="mt-10 glass-card rounded-2xl p-6 border border-brand-600/20 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-white mb-1">Expand your reach</p>
                <p className="text-sm text-slate-400">Add more placements or regions to grow your campaign impact.</p>
              </div>
              <Link href="/order-wizard" className="shrink-0 inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold btn-primary">
                <Zap size={14} />
                Book Another Ad
                <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {/* Support Ticket Form */}
          <div className="mt-10">
            <SupportTicketForm
              userId={resolvedUser.id}
              orders={orders.map((o) => ({ id: o.id, orderNumber: o.orderNumber }))}
            />
          </div>

          {/* Invoices & Billing History */}
          <div className="mt-8">
            <InvoicesSection orders={orders} loading={ordersLoading} />
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
